import { FC, forwardRef, useEffect, useMemo } from 'react'
import { Assumption, FieldFilterRestrictionType } from '@portal/pkg-api/gen/navigator'
import { FieldError, useController, useFormContext, useWatch } from 'react-hook-form'
import { FormControl, FormHelperText, Grid, IconButton, MenuItem, Select } from '@mui/material'
import { CategorizedAssumptionPicker } from '@portal/pkg-filter-criteria/components'
import { useQuery } from '@tanstack/react-query'
import { AssumptionQueries } from '@portal/pkg-filter-criteria/data'
import type { ParseKeys } from 'i18next'
import { useTranslation } from 'react-i18next'
import { emptyFn } from '@portal/pkg-utils'
import CloseIcon from '@mui/icons-material/Close'
import { FilterCriteriaValue } from './FilterCriteriaValue'
import { FieldFilterRestrictionOperator } from '@portal/pkg-api/gen/admin'

export interface FilterCriteriaRowProps {
  name: string
  entityTypeId?: number
  onRemove?: () => void
}

const ErrorHelperText: FC<{ error?: FieldError }> = ({ error }) => {
  const { t } = useTranslation()

  if (error) {
    return (
      <FormHelperText error sx={{ pl: 1, mb: 1 }}>
        {t(error.message as ParseKeys)}
      </FormHelperText>
    )
  }
}

export const FilterCriteriaRow = forwardRef<HTMLDivElement, FilterCriteriaRowProps>(
  ({ name, entityTypeId, onRemove = emptyFn }, ref) => {
    const { t } = useTranslation()

    const { data: assumptions } = useQuery(AssumptionQueries.assumptions(entityTypeId))

    const {
      field: { value: operand1Value, onChange: operand1OnChange, ...operand1Field },
      fieldState: { error: operand1Error },
    } = useController({
      name: `${name}.operand1`,
    })

    const assumption = useMemo(() => {
      if (assumptions && operand1Value) {
        return assumptions.find((a) => a.id === operand1Value) ?? null
      }

      return null
    }, [assumptions, operand1Value])

    const handleOperand1Change = (newAssumption: Assumption | null) => {
      operand1OnChange(newAssumption?.id ?? null)
    }

    const {
      field: operatorField,
      fieldState: { error: operatorError },
    } = useController({ name: `${name}.operator` })

    const { getValues, resetField } = useFormContext()
    const watchedOperator: FieldFilterRestrictionType | null = useWatch({
      name: `${name}.operator`,
    })
    const operator = useMemo<FieldFilterRestrictionOperator | null>(
      () => watchedOperator || getValues(`${name}.operator`),
      [getValues, name, watchedOperator],
    )

    const {
      field: operand2ValueField,
      fieldState: { error: operand2ValueError },
    } = useController({ name: `${name}.operand2Value` })

    // Reset the operand2Value field only when operator or assumption changes
    useEffect(() => {
      resetField(`${name}.operand2Value`)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operator, assumption])

    return (
      <Grid container columnSpacing={1} sx={{ alignItems: 'center' }} ref={ref}>
        <Grid size={4}>
          <FormControl fullWidth error={!!operand1Error}>
            <CategorizedAssumptionPicker
              value={assumption}
              onChange={handleOperand1Change}
              filters={{
                entityTypeId,
                onlyFilterable: true,
              }}
              {...operand1Field}
              inputProps={{ error: !!operand1Error }}
              label="Assumption"
            />
          </FormControl>
        </Grid>
        <Grid size={3}>
          <FormControl fullWidth error={!!operatorError}>
            <Select disabled={!assumption} size="small" {...operatorField}>
              {assumption?.filterRestrictionTypes?.map((r) => (
                <MenuItem key={r} value={r}>
                  {t([
                    `filterCriteria.operator.${assumption.type}.${r}` as ParseKeys,
                    `filterCriteria.operator.${r}` as ParseKeys,
                  ])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={4.5}>
          <FilterCriteriaValue
            field={{ ...operand2ValueField, error: !!operand2ValueError }}
            assumption={assumption}
            operator={operator}
          />
        </Grid>
        <Grid size={0.5}>
          <IconButton size="small" onClick={onRemove}>
            <CloseIcon />
          </IconButton>
        </Grid>

        <Grid size={4}>
          <ErrorHelperText error={operand1Error} />
        </Grid>
        <Grid size={3}>
          <ErrorHelperText error={operatorError} />
        </Grid>
        <Grid size={4.5}>
          <ErrorHelperText error={operand2ValueError} />
        </Grid>
      </Grid>
    )
  },
)
FilterCriteriaRow.displayName = 'FilterCriteriaRow'
